import React, { useState, useMemo, useCallback, useRef } from 'react'
import { Checkbox } from '@you/ui'
import { useOrganizationList } from '@/api/hooks/useOrganization'
import { organizationService } from '@/api/services/organization.service'
import type { DepartmentTreeNode } from '@/api/types/organization.type'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import TreeNode from '@/components/Agents/TreeNode'

interface DepartmentSelectorProps {
  isLinkage: boolean
  agentTableId?: string
  onSelectionChange?: (selectedIds: string[]) => void
}

const DepartmentSelector: React.FC<DepartmentSelectorProps> = ({
  isLinkage,
  agentTableId,
  onSelectionChange,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [loadedOrganizations, setLoadedOrganizations] = useState<Set<string>>(new Set())
  const [organizationTree, setOrganizationTree] = useState<DepartmentTreeNode[]>([])
  const [loadingOrgs, setLoadingOrgs] = useState<Set<string>>(new Set())

  // 使用ref存储isLinkage，避免props变化导致重新渲染
  const isLinkageRef = useRef(isLinkage)
  
  // 更新ref值
  React.useEffect(() => {
    isLinkageRef.current = isLinkage
  }, [isLinkage])

  const { currentWorkspace } = useWorkspaceStore()

  // 查询组织列表
  const { data: organizationList, isLoading: isLoadingOrgs } = useOrganizationList({
    workspaceId: currentWorkspace?.id || '',
  })

  // 初始化组织树
  React.useEffect(() => {
    if (organizationList?.teams) {
      const orgNodes: DepartmentTreeNode[] = organizationList.teams.map(org => ({
        id: org.id,
        name: org.name,
        isRoot: true,
        subDepartments: [],
      }))
      setOrganizationTree(orgNodes)
    }
  }, [organizationList])

  // 查询部门树数据的函数 - 优化异步处理
  const fetchDepartmentTree = useCallback(async (teamId: string) => {
    if (loadedOrganizations.has(teamId)) return

    setLoadingOrgs(prev => new Set(prev).add(teamId))
    
    try {
      const response = await organizationService.getDepartmentTreeToAgent({
        agentTableId: agentTableId || '',
        teamId,
        workspaceId: currentWorkspace?.id || '',
      })
      
      if (response?.departments) {
        setOrganizationTree(prev => prev.map(org => {
          if (org.id === teamId) {
            return { ...org, subDepartments: response.departments }
          }
          return org
        }))
        setLoadedOrganizations(prev => new Set(prev).add(teamId))
      }
    } catch (error) {
      console.error('加载部门树失败:', error)
    } finally {
      setLoadingOrgs(prev => {
        const newSet = new Set(prev)
        newSet.delete(teamId)
        return newSet
      })
    }
  }, [loadedOrganizations, agentTableId, currentWorkspace?.id])

  // 递归计算子部门选中数量
  const getChildSelectedCount = useCallback((node: DepartmentTreeNode, currentSelectedIds: Set<string>): number => {
    if (!node.subDepartments || node.subDepartments.length === 0) {
      return 0
    }
    return node.subDepartments.reduce((count, child) => {
      const childSelected = currentSelectedIds.has(child.id) ? 1 : 0
      return count + childSelected + getChildSelectedCount(child, currentSelectedIds)
    }, 0)
  }, [])

  // 递归计算子部门总数
  const getChildCount = useCallback((node: DepartmentTreeNode): number => {
    if (!node.subDepartments || node.subDepartments.length === 0) {
      return 0
    }
    return node.subDepartments.reduce((count, child) => {
      return count + 1 + getChildCount(child)
    }, 0)
  }, [])

  // 递归选中/取消选中子部门 - 只收集ID，不触发状态更新
  const collectChildrenIds = useCallback((node: DepartmentTreeNode, checked: boolean, targetIds: Set<string>) => {
    if (checked) {
      targetIds.add(node.id)
    } else {
      targetIds.delete(node.id)
    }

    if (node.subDepartments) {
      node.subDepartments.forEach((child) => {
        collectChildrenIds(child, checked, targetIds)
      })
    }
  }, [])

  // 处理部门选择 - 优化状态更新逻辑
  const handleToggleSelect = useCallback((id: string, checked: boolean) => {
    setSelectedIds(prevSelectedIds => {
      const newSelectedIds = new Set(prevSelectedIds)
      const currentIsLinkage = isLinkageRef.current
      
      if (currentIsLinkage) {
        // 联动模式：找到对应的节点并递归处理
        const findAndSelectNode = (nodes: DepartmentTreeNode[]): boolean => {
          for (const node of nodes) {
            if (node.id === id) {
              // 使用collectChildrenIds收集所有需要变更的ID，一次性更新状态
              collectChildrenIds(node, checked, newSelectedIds)
              return true
            }
            if (node.subDepartments && findAndSelectNode(node.subDepartments)) {
              return true
            }
          }
          return false
        }
        
        findAndSelectNode(organizationTree)
      } else {
        // 非联动模式：只处理当前节点
        if (checked) {
          newSelectedIds.add(id)
        } else {
          newSelectedIds.delete(id)
        }
      }
      
      const selectedIdsArray = Array.from(newSelectedIds)
      onSelectionChange?.(selectedIdsArray)
      return newSelectedIds
    })
  }, [organizationTree, collectChildrenIds, onSelectionChange])

  // 处理展开/收起 - 优化状态更新逻辑
  const handleToggleExpand = useCallback((id: string) => {
    setExpandedIds(prevExpandedIds => {
      const newExpandedIds = new Set(prevExpandedIds)
      if (newExpandedIds.has(id)) {
        newExpandedIds.delete(id)
      } else {
        newExpandedIds.add(id)
      }
      return newExpandedIds
    })
  }, [])

  // 全选/取消全选
  const handleSelectAll = useCallback((checked: boolean) => {
    setSelectedIds(() => {
      const newSelectedIds = new Set<string>()
      
      const selectAllNodes = (nodes: DepartmentTreeNode[]) => {
        nodes.forEach((node) => {
          if (checked) {
            newSelectedIds.add(node.id)
          }
          if (node.subDepartments) {
            selectAllNodes(node.subDepartments)
          }
        })
      }

      selectAllNodes(organizationTree)
      
      const selectedIdsArray = Array.from(newSelectedIds)
      onSelectionChange?.(selectedIdsArray)
      return newSelectedIds
    })
  }, [organizationTree, onSelectionChange])

  // 计算全选状态
  const allSelectedCount = useMemo(() => {
    if (organizationTree.length === 0) return 0
    return getChildSelectedCount({ id: '', name: '', subDepartments: organizationTree }, selectedIds)
  }, [organizationTree, selectedIds, getChildSelectedCount])

  const totalCount = useMemo(() => {
    if (organizationTree.length === 0) return 0
    return getChildCount({ id: '', name: '', subDepartments: organizationTree })
  }, [organizationTree, getChildCount])

  const isAllSelected = totalCount > 0 && allSelectedCount === totalCount

  if (isLoadingOrgs) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, index) => (
          <div
            key={index}
            className="flex items-center px-3 py-2 rounded-lg animate-pulse"
            style={{
              backgroundColor: '#F5F4FE78',
              borderRadius: '8px',
              height: '42px',
            }}
          >
            <div className="w-4 h-4 bg-gray-200 rounded mr-3"></div>
            <div className="w-4 h-4 bg-gray-200 rounded mr-2"></div>
            <div className="flex-1 h-4 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    )
  }

  if (!organizationTree || organizationTree.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        暂无组织数据
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* 全选控制 */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
        <div className="flex items-center">
          <Checkbox
            checked={isAllSelected}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="data-[state=checked]:bg-[#8752FF] data-[state=checked]:border-[#8752FF]"
          />
          <span className="ml-2 font-medium text-gray-700">全选</span>
        </div>
        <span className="text-sm text-gray-500">
          已选择 {allSelectedCount}/{totalCount} 项
        </span>
      </div>

      {/* 组织树 */}
      <div className="space-y-1">
        {organizationTree.map((node) => (
          <TreeNode
            key={node.id}
            node={node}
            level={0}
            selectedIds={selectedIds}
            expandedIds={expandedIds}
            loadingOrgs={loadingOrgs}
            onToggleExpand={handleToggleExpand}
            onToggleSelect={handleToggleSelect}
            getChildSelectedCount={getChildSelectedCount}
            getChildCount={getChildCount}
            fetchDepartmentTree={fetchDepartmentTree}
            isLinkageRef={isLinkageRef}
          />
        ))}
      </div>
    </div>
  )
}

export default DepartmentSelector